import React, { InputHTMLAttributes, ReactNode, useEffect, useState, } from 'react';
import { UseFormRegisterReturn, UseFormSetValue, SubmitHandler, useForm, UseFormGetValues } from 'react-hook-form';
import { Table, Tooltip, Button, Modal, TextInput, Radio } from "flowbite-react";
import CustomThemeTable from '../CustomThemes/CustomThemeTable';
import CustomThemeModal from '../CustomThemes/CustomThemeModal';
import { useLoader } from '../../hooks/useLoader';
import InformationTag from '../InfoTag/InfoTag';

import { MdDelete } from "react-icons/md";
import { CiSquarePlus } from "react-icons/ci";
import { FaPlus } from "react-icons/fa";
import { HiOutlineExclamationCircle } from "react-icons/hi";

import { useGetMembersByRequestID } from '../../hooks/Member/useGetMembersByRequestID';
import ApiServiceClass from '../../services/ApiServiceClass';
import { useAuth } from '../../hooks/useAuth';

interface FormMembersFieldProps extends InputHTMLAttributes<HTMLInputElement> {
    itemNumber?: number;
    requestID?: string | null;
    registerItemizedField: UseFormRegisterReturn;
    setItemizedFieldValue: UseFormSetValue<any>;
    getItemizedFieldValue: UseFormGetValues<any>;
    labelName?: ReactNode;
    infoTagMessage?: string | " ";
    fieldName: string | "field";
    errorsItemizedField?: Record<string, any>; // Form state errors
}

type MembersFieldType = {
    id: number,
    is_primary: boolean,
    name: string,
    upi: string
}

type MembersForSearchType = {
    id: number,
    name: string,
    upi: string
}

const FormMembersField: React.FC<FormMembersFieldProps> = ({ registerItemizedField, requestID, setItemizedFieldValue, getItemizedFieldValue, labelName, fieldName, errorsItemizedField, infoTagMessage, itemNumber, ...rest }) => {
    const request_id: number = requestID !== null && requestID !== undefined ? parseInt(requestID) : 0;
    const loader = useLoader();
    const { fetMembersByRequestID } = useGetMembersByRequestID(request_id);
    const [currentMembers, setCurrentMembers] = useState<MembersFieldType[]>([]);
    const [currentMembersForSearch, setCurrentMembersForSearch] = useState<MembersForSearchType[]>([]);
    const [currentFilteredMembers, setCurrentFilteredMembers] = useState<MembersForSearchType[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [searchColloborator, setSearchCollaborator] = useState<string>("");

    const auth = useAuth();

    const loadData = async () => {
        try {
            loader?.setIsLoading(true);
            const members = await fetMembersByRequestID();
            setCurrentMembers(members);
        } catch (error) {
            console.error(error);
        } finally {
            loader?.setIsLoading(false);
        }
    }

    const loadMembersForSearch = async () => {
        try {
            loader?.setIsLoading(true);
            const membersNotInRequest = await ApiServiceClass.getMembersNotInRequest(request_id);
            setCurrentMembersForSearch(membersNotInRequest);
            setCurrentFilteredMembers(membersNotInRequest);
        } catch (error) {
            console.error(error);
        } finally {
            loader?.setIsLoading(false);
        }
    }

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchCollaborator(event.target.value);
        const filteredMembers = currentMembersForSearch.filter(item =>
            item.name.toLowerCase().includes(event.target.value.toLowerCase())
        );

        setCurrentFilteredMembers(filteredMembers);
    }

    const handleDeleteItem = (id: number, isPrimary: boolean) => {
        if (isPrimary) {
            setOpenDeleteModal(true);
        } else {
            try {
                loader?.setIsLoading(true);
                ApiServiceClass.deleteMemberFromRequest(request_id, id)
                    .then(() => {
                        loadData();
                    })
            } catch (error) {
                console.error(error);
            } finally {
                loader?.setIsLoading(false);
            }
        }
    }

    const addCollaborator = () => {
        setOpenModal(true);
    }

    const addMember = (id: number) => {
        console.log('Add member ' + id);
        try {
            loader?.setIsLoading(true);
            ApiServiceClass.addMemberToRequest(request_id, id)
                .then(() => {
                    loadData();
                    loadMembersForSearch();
                    loader?.setIsLoading(false);
                })
        } catch (error) {
            setOpenModal(false);
            console.error(error);
        } finally {
            setOpenModal(false);
        }
    }

    const handleChangePrimary = async (id: number) => {
        try {
            loader?.setIsLoading(true);
            ApiServiceClass.changePrimaryRequestor(request_id, id)
                .then(() => {
                    loadData();
                    loadMembersForSearch();
                    loader?.setIsLoading(false);
                })
        } catch (error) {
            loader?.setIsLoading(false);
            setOpenModal(false);
            console.error(error);
        } finally {
            loader?.setIsLoading(false);
            setOpenModal(false);
        }
    }

    useEffect(() => {
        loadData();
        loadMembersForSearch();
    }, []);
    return (
        <div className="mb-8">
            <div className="flex">
                <div className="flex-none w-5 text-sm">{itemNumber}.</div>
                <div className="text-sm grid grid-cols-2"><div>{labelName}</div> <div className="ml-2"><InformationTag message={infoTagMessage} /></div></div>
            </div>
            <div className="flex">
                <div className="flex-none w-5"></div>
                <div className="grow mt-3">
                    <input
                        {...rest}
                        {...registerItemizedField} // This spreads the register function correctly
                    />
                    <Table hoverable theme={CustomThemeTable}>
                        <Table.Head>
                            <Table.HeadCell>Name</Table.HeadCell>
                            <Table.HeadCell>Primary</Table.HeadCell>
                            <Table.HeadCell>
                                <span className="sr-only">Edit & Delete</span>
                            </Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">

                            {currentMembers.map((item, index) => {
                                return (
                                    <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800" key={index}>
                                        <Table.Cell>
                                            {item.name}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {
                                                item.is_primary && <Radio id="namefield" name="namefield" defaultChecked onChange={() => handleChangePrimary(item.id)} />
                                            }
                                            {
                                                !item.is_primary && <Radio id="namefield" name="namefield" onChange={() => handleChangePrimary(item.id)} />
                                            }
                                        </Table.Cell>
                                        <Table.Cell>
                                            {item.upi != auth?.upi && <div className="font-thin text-base text-red-600 hover:underline dark:text-red-400 flex items-center">
                                                <Tooltip content="Delete" style="dark">
                                                    <MdDelete onClick={() => handleDeleteItem(item.id, item.is_primary)} className="cursor-pointer" />
                                                </Tooltip>
                                            </div>}
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            })}

                        </Table.Body>
                    </Table>
                    <div className="mt-5">
                        <Button onClick={() => addCollaborator()} size="xs" color="blue">
                            <CiSquarePlus className="mr-2 h-5 w-5" />
                            Add Collaborator
                        </Button>
                    </div>

                    <Modal show={openModal} onClose={() => setOpenModal(false)} size={'sm'} theme={CustomThemeModal}>
                        <Modal.Body>
                            <button
                                aria-label="Close"
                                className="absolute top-2 right-2  z-20 ml-auto inline-flex items-center rounded-lg bg-white border-gray-500 border-solid border-2 p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
                                type="button"
                                onClick={() => setOpenModal(false)}
                            >
                                <svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                            <div className="space-y-6">
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white">Select Collaborator</h3>
                            </div>
                            <div className="mt-2">
                                <TextInput
                                    id="searchCollaborators"
                                    type="text"
                                    value={searchColloborator}
                                    onChange={handleSearch}
                                />
                            </div>
                            <div className="mt-5 h-56 max-h-52 overflow-y-scroll">

                                <Table hoverable>
                                    <Table.Body className="divide-y">

                                        {currentFilteredMembers.map((item, index) => {
                                            return (
                                                <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800" key={index}>
                                                    <Table.Cell>
                                                        {item.name}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        <div className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-cyan-500 dark:bg-cyan-800 dark:text-cyan-200">
                                                            <FaPlus className="h-3 w-3 text-sm cursor-pointer" onClick={() => addMember(item.id)} />
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                            )
                                        })}


                                    </Table.Body>
                                </Table>

                            </div>
                        </Modal.Body>
                    </Modal>

                    <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} theme={CustomThemeModal} popup>
                        <Modal.Body>
                            <div className="text-center">
                                <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200 mt-3" />
                                <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                                    You cannot delete a primary requestor. Please select a different primary requestor. At least one requestor is required.
                                </h3>
                                <div className="flex justify-center">
                                    <Button color="gray" onClick={() => setOpenDeleteModal(false)}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </Modal.Body>
                    </Modal>

                </div>
            </div>
        </div>
    )
}

export default FormMembersField;